import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./ContactEmerald.css";

/**
 * Contact section – Emerald Appraisal Services
 *
 * Changes:
 *  - Removed physical office address block per client request
 *  - Email updated to office@emeraldappraisalservices.com
 *  - Purpose wording: “Purchases & Refinances” (no “lending”)
 *  - Added “Sketching Only” and “Vacant Land” to purposes
 *  - Subtle emerald theme accents
 *  - Updated all SVG icons to Material Icons
 *  - Integrated Formspree + Google reCAPTCHA
 */

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export default function ContactEmerald({
  phone = "253.359.7574",
  email = "office@emeraldappraisalservices.com",
  onSubmit,
  imageSrc,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    purpose: "Purchases & Refinances",
    message: "",
    // simple honeypot
    company: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ sending: false, ok: null, msg: "" });
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const recaptchaRef = useRef(null);

  const telHref = `tel:+1${phone.replace(/\D/g, "")}`;
  const mailHref = `mailto:${email}`;

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email.";
    if (!/^\+?[\d\s().-]{7,}$/.test(form.phone))
      next.phone = "Enter a valid phone number.";
    if (!form.message.trim() || form.message.trim().length < 10)
      next.message = "Please add a short message (10+ characters).";
    // honeypot
    if (form.company) next.company = "Spam detected.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
    if (status.ok !== null || status.msg) {
      setStatus({ sending: false, ok: null, msg: "" });
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (status.ok !== null || status.msg) {
      setStatus({ sending: false, ok: null, msg: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!recaptchaToken) {
      setStatus({
        sending: false,
        ok: false,
        msg: "Please complete the reCAPTCHA before submitting.",
      });
      return;
    }

    if (!FORMSPREE_ENDPOINT) {
      setStatus({
        sending: false,
        ok: false,
        msg: "Form endpoint is not configured. Please contact us by phone or email.",
      });
      return;
    }

    try {
      setStatus({ sending: true, ok: null, msg: "" });

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        purpose: form.purpose,
        message: form.message.trim(),
        submittedAt: new Date().toISOString(),
        // Formspree-compatible recaptcha field
        "g-recaptcha-response": recaptchaToken,
      };

      // Send to Formspree
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        // Optional: also call custom handler if provided
        if (onSubmit) {
          await onSubmit(payload);
        }

        setStatus({
          sending: false,
          ok: true,
          msg: "Thanks! We'll get back to you shortly.",
        });

        setForm({
          name: "",
          email: "",
          phone: "",
          purpose: "Purchases & Refinances",
          message: "",
          company: "",
        });

        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        const errorMsg =
          data?.error ||
          (Array.isArray(data?.errors) && data.errors[0]?.message) ||
          "Something went wrong. Please try again or call us.";
        setStatus({
          sending: false,
          ok: false,
          msg: errorMsg,
        });
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setStatus({
        sending: false,
        ok: false,
        msg: "Network error. Please try again or call us.",
      });
    }
  };

  return (
    <section className="ContactEmerald" aria-labelledby="ce-title">
      <div className="ContactEmerald__background">
        <div className="ContactEmerald__bg-shape ContactEmerald__bg-shape--1"></div>
        <div className="ContactEmerald__bg-shape ContactEmerald__bg-shape--2"></div>
        <div className="ContactEmerald__bg-shape ContactEmerald__bg-shape--3"></div>
      </div>

      <div className="ContactEmerald__container">
        {/* LEFT: Contact details (no physical office) */}
        <aside className="ContactEmerald__info">
          <div className="ContactEmerald__info-content">
            <p className="ContactEmerald__eyebrow">Get In Touch</p>
            <h1 id="ce-title" className="ContactEmerald__title">
              Let's discuss your
              <span className="ContactEmerald__title-accent">
                {" "}
                appraisal needs
              </span>
            </h1>
            <p className="ContactEmerald__lead">
              Have questions about scope, timing, or service areas? We provide
              certified residential valuations across the Puget Sound—delivering
              clear, defensible opinions of value with transparent communication.
            </p>

            <div className="ContactEmerald__contact-methods">
              <div className="ContactEmerald__method">
                <div className="ContactEmerald__method-icon">
                  <span className="material-icons">phone</span>
                </div>
                <div className="ContactEmerald__method-content">
                  <h3>Call Us Directly</h3>
                  <a className="ContactEmerald__link" href={telHref}>
                    {phone}
                  </a>
                </div>
              </div>

              <div className="ContactEmerald__method">
                <div className="ContactEmerald__method-icon">
                  <span className="material-icons">email</span>
                </div>
                <div className="ContactEmerald__method-content">
                  <h3>Send Us an Email</h3>
                  <a className="ContactEmerald__link" href={mailHref}>
                    {email}
                  </a>
                </div>
              </div>
            </div>

            <div className="ContactEmerald__service-info">
              <div className="ContactEmerald__service-card">
                <div className="ContactEmerald__service-icon">
                  <span className="material-icons">schedule</span>
                </div>
                <div className="ContactEmerald__service-details">
                  <h4>Typical Turnaround</h4>
                  <p>
                    <strong>3–5 business days</strong> after the site visit
                  </p>
                  <span className="ContactEmerald__service-note">
                    Rush options available
                  </span>
                </div>
              </div>
            </div>

            {imageSrc && (
              <div className="ContactEmerald__visual">
                <div className="ContactEmerald__image-container">
                  <img
                    src={imageSrc}
                    alt="Emerald Appraisal Services professional team"
                    className="ContactEmerald__image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="ContactEmerald__image-overlay">
                    <span>Puget Sound Residential Expertise</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* RIGHT: Form */}
        <div className="ContactEmerald__form-section">
          <div className="ContactEmerald__form-container">
            <div className="ContactEmerald__form-header">
              <h2 className="ContactEmerald__form-title">Send us a message</h2>
              <p className="ContactEmerald__form-subtitle">
                Complete the form below and we'll respond within 24 hours
              </p>
            </div>

            <form
              className="ContactEmerald__form"
              onSubmit={handleSubmit}
              noValidate
            >
              {/* Honeypot */}
              <input
                type="text"
                name="company"
                autoComplete="off"
                tabIndex="-1"
                className="ContactEmerald__hp"
                value={form.company}
                onChange={handleChange}
              />

              <div className="ContactEmerald__form-grid">
                <div className="ContactEmerald__field-group">
                  <div className="ContactEmerald__field">
                    <label
                      htmlFor="name"
                      className="ContactEmerald__field-label"
                    >
                      Full Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={handleChange}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "err-name" : undefined}
                      className={
                        errors.name ? "ContactEmerald__input--error" : ""
                      }
                      required
                    />
                    {errors.name && (
                      <div id="err-name" className="ContactEmerald__error">
                        <span className="material-icons ContactEmerald__error-icon">
                          error
                        </span>
                        {errors.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ContactEmerald__field-group">
                  <div className="ContactEmerald__field">
                    <label
                      htmlFor="email"
                      className="ContactEmerald__field-label"
                    >
                      Email Address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "err-email" : undefined}
                      className={
                        errors.email ? "ContactEmerald__input--error" : ""
                      }
                      required
                    />
                    {errors.email && (
                      <div id="err-email" className="ContactEmerald__error">
                        <span className="material-icons ContactEmerald__error-icon">
                          error
                        </span>
                        {errors.email}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ContactEmerald__field-group">
                  <div className="ContactEmerald__field">
                    <label
                      htmlFor="phone"
                      className="ContactEmerald__field-label"
                    >
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(253) 555-0123"
                      value={form.phone}
                      onChange={handleChange}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? "err-phone" : undefined}
                      className={
                        errors.phone ? "ContactEmerald__input--error" : ""
                      }
                      required
                    />
                    {errors.phone && (
                      <div id="err-phone" className="ContactEmerald__error">
                        <span className="material-icons ContactEmerald__error-icon">
                          error
                        </span>
                        {errors.phone}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ContactEmerald__field-group">
                  <div className="ContactEmerald__field">
                    <label
                      htmlFor="purpose"
                      className="ContactEmerald__field-label"
                    >
                      Purpose of Appraisal
                    </label>
                    <div className="ContactEmerald__select-wrapper">
                      <select
                        id="purpose"
                        name="purpose"
                        value={form.purpose}
                        onChange={handleChange}
                        className="ContactEmerald__select"
                      >
                        <option>Purchases & Refinances</option>
                        <option>Estate / Date-of-Death</option>
                        <option>Divorce & Separation</option>
                        <option>Pre-Listing / Pre-Purchase</option>
                        <option>Assessment Appeal</option>
                        <option>Sketching Only</option>
                        <option>Vacant Land</option>
                        <option>Other</option>
                      </select>
                      <div className="ContactEmerald__select-arrow">
                        <span className="material-icons">expand_more</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ContactEmerald__field-group">
                <div className="ContactEmerald__field">
                  <label
                    htmlFor="message"
                    className="ContactEmerald__field-label"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    placeholder="Tell us about the property, location, timing requirements, and any specific needs..."
                    value={form.message}
                    onChange={handleChange}
                    aria-invalid={!!errors.message}
                    aria-describedby={
                      errors.message ? "err-message" : undefined
                    }
                    className={
                      errors.message
                        ? "ContactEmerald__input--error ContactEmerald__textarea"
                        : "ContactEmerald__textarea"
                    }
                    required
                  />
                  {errors.message && (
                    <div id="err-message" className="ContactEmerald__error">
                      <span className="material-icons ContactEmerald__error-icon">
                        error
                      </span>
                      {errors.message}
                    </div>
                  )}
                </div>
              </div>

              {/* reCAPTCHA */}
              {RECAPTCHA_SITE_KEY && (
                <div className="ContactEmerald__field-group ContactEmerald__recaptcha">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                  />
                </div>
              )}

              <div className="ContactEmerald__form-actions">
                <button
                  type="submit"
                  className="ContactEmerald__submit"
                  disabled={status.sending}
                  aria-busy={status.sending}
                >
                  <span className="ContactEmerald__submit-text">
                    {status.sending ? "Sending Message..." : "Send Message"}
                  </span>
                  <div className="ContactEmerald__submit-icon">
                    {status.sending ? (
                      <span className="material-icons ContactEmerald__loading">
                        refresh
                      </span>
                    ) : (
                      <span className="material-icons">arrow_forward</span>
                    )}
                  </div>
                  <div className="ContactEmerald__submit-shine"></div>
                </button>

                {status.ok === true && (
                  <div className="ContactEmerald__status ContactEmerald__status--success">
                    <div className="ContactEmerald__status-icon">
                      <span className="material-icons">check_circle</span>
                    </div>
                    <div className="ContactEmerald__status-content">
                      <strong>Message Sent Successfully!</strong>
                      <p>{status.msg}</p>
                    </div>
                  </div>
                )}
                {status.ok === false && (
                  <div className="ContactEmerald__status ContactEmerald__status--error">
                    <div className="ContactEmerald__status-icon">
                      <span className="material-icons">error</span>
                    </div>
                    <div className="ContactEmerald__status-content">
                      <strong>Something Went Wrong</strong>
                      <p>{status.msg}</p>
                    </div>
                  </div>
                )}
              </div>
            </form>

            <div className="ContactEmerald__privacy">
              <div className="ContactEmerald__privacy-lock">
                <span className="material-icons">lock</span>
              </div>
              <p>
                Your information is secure. By submitting this form, you agree
                to our{" "}
                <a
                  href="/privacy"
                  className="ContactEmerald__privacy-link"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/terms" className="ContactEmerald__privacy-link">
                  Terms of Service
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
